import React, { useState, useRef } from 'react'
import PropTypes from 'prop-types'
import isEqual from 'lodash/isEqual'
import cx from 'classnames'
import IntersectionObserver from '@researchgate/react-intersection-observer'
import { useAvailability } from '@local/do-secundo-availability-provider'
import { useReorderQuery } from '../ReorderQuery/ReorderQuery'
import { useFlag, FF } from '@local/do-secundo-feature-flag'
import { cdnUrl } from '../../utils/cdn'
import { getFastRect } from '../../utils/dimensions'
import { formatPhoneNumber } from '../../utils/phone-number'

import PhoneImg from '../../assets/phone.svg'

import { RestaurantAddress } from '../RestaurantAddress/RestaurantAddress'
import { FulfillmentSelector } from '../FulfillmentSelector/FulfillmentSelector'
import { Modal } from '@local/do-secundo-modal'
import { SocialMediaButtons } from '../SocialMediaButtons/SocialMediaButtons'
import { RestaurantNotification } from '../RestaurantNotification/RestaurantNotification'
import { ReordersOrPopularItemsBanner } from './ReordersOrPopularItemsBanner'

import { RestaurantHours } from '../RestaurantHours/RestaurantHours'
import { RestaurantSchedule } from '@local/do-secundo-restaurant-schedule'
import styles from './RestaurantHeader.module.css'
import { MealSelectorPrompt } from '../MealSelector/MealSelectorPrompt/MealSelectorPrompt'
import { useVariant } from '../../hooks/use-variant'
import {
  XP_MEAL_SELECTOR,
  XP_PROMO_BANNERS
} from '../ExperimentsProvider/experiment-definitions'
import { PromoBanner } from '@local/do-secundo-promo-banner'
import { MenuNavigationModule } from './MenuNavigationModule'

// separate export to allow testing, https://github.com/enzymejs/enzyme/issues/2196 <-- enzyme does not work well with memoized functional components
export const RestaurantHeaderInner = ({
  restaurant: {
    imageUrl,
    bannerUrls,
    whiteLabelName,
    location,
    logoUrls = {},
    schedule,
    description,
    socialMediaLinks,
    timeZoneId,
    onlineOrderingEnabled
  },
  activeMenu,
  menus,
  menusLength,
  activeItem,
  publicApi
}) => {
  const { address1, address2, city, state, zip, phone } = location

  const useBannerImage = useFlag(FF.BANNER_IMAGE_FIX)
  const nv5SEOEnabled = useFlag(FF.NV5_SEO)

  const mealSelectorVariant = !!useVariant(XP_MEAL_SELECTOR.experimentName)
  const promoBannerVariant = !!useVariant(XP_PROMO_BANNERS.experimentName)
  const bannerUrl = useBannerImage
    ? bannerUrls && bannerUrls.raw
    : imageUrl && cdnUrl(imageUrl)
  const [shouldShowInfo, editShowInfo] = useState(false)
  const { orderingAvailable } = useAvailability()
  const stickyRef = useRef()
  const { hasReorders } = useReorderQuery()
  const [showStickyShadow, setShowStickyShadow] = useState(false)

  /**
   * Show sticky nav when:
   * 1. menusLength > 1
   * 2. menuLengths === 1 and menus.groups.length > 1
   *
   * @returns {*|boolean}
   */
  const shouldShowStickyNav = () => {
    const hasMultipleMenus = menusLength > 1
    const multipleMenuGroups =
      activeMenu && activeMenu.groups && activeMenu.groups.length > 1
    const showStickyNav = hasMultipleMenus || multipleMenuGroups

    return showStickyNav
  }
  const moreThanOneMenu = menusLength > 1
  const calcHeaderOffset = async () => {
    const { height } = await getFastRect(stickyRef.current)
    return height
  }

  // Call directly from the React module to make this mockable
  React.useImperativeHandle(publicApi, () => {
    return {
      calcHeaderOffset
    }
  })

  const showStickyShadowCallback = (entry) => {
    setShowStickyShadow(!entry.isIntersecting)
  }

  return (
    <>
      <div
        className={styles.header}
        role='group'
        aria-label='restaurant information'
      >
        <div className={styles.notification}>
          <RestaurantNotification />
        </div>
        <div className={styles.banner}>
          <div className={styles.information}>
            {logoUrls && logoUrls.small ? (
              <div
                className={styles.logo}
                style={{ backgroundImage: `url(${logoUrls.small})` }}
              />
            ) : (
              <div className={cx(styles.logo, styles.empty)} />
            )}

            <h1
              className={cx(styles.name, 'type-headline-2')}
              itemProp={nv5SEOEnabled && 'name'}
            >
              {whiteLabelName}
            </h1>
            {nv5SEOEnabled && phone && (
              <meta itemProp='telephone' content={phone} />
            )}
            <div className={cx(styles.info, 'type-default')}>
              <div
                className='mb-1'
                itemProp={nv5SEOEnabled && 'address'}
                itemScope={nv5SEOEnabled}
                itemType={nv5SEOEnabled && 'https://schema.org/PostalAddress'}
              >
                {nv5SEOEnabled && (
                  <>
                    <meta
                      itemProp='streetAddress'
                      content={`${address1}${address2 ? `, ${address2}` : ''}`}
                    />
                    <meta itemProp='addressLocality' content={city} />
                    <meta itemProp='addressRegion' content={state} />
                    <meta itemProp='postalCode' content={zip} />
                  </>
                )}
                <RestaurantAddress
                  address1={address1}
                  address2={address2}
                  city={city}
                  state={state}
                />
              </div>
              <RestaurantSchedule
                schedule={schedule}
                orderingAvailable={orderingAvailable}
                timeZoneId={timeZoneId}
                onlineOrderingEnabled={onlineOrderingEnabled}
              />
              <button
                data-testid='rx-info-button'
                className={styles.infoButton}
                onClick={() => editShowInfo(!shouldShowInfo)}
              >
                More Info
              </button>
            </div>
          </div>
          {shouldShowInfo && (
            <Modal onClose={() => editShowInfo(false)} header={whiteLabelName}>
              <div className={cx(styles.moreInfo, 'type-default')}>
                <div className='py-2 -ml-1'>
                  <RestaurantSchedule
                    schedule={schedule}
                    orderingAvailable={orderingAvailable}
                    timeZoneId={timeZoneId}
                    onlineOrderingEnabled={onlineOrderingEnabled}
                  />
                </div>
                <div className={styles.infoIcons}>
                  {socialMediaLinks && (
                    <SocialMediaButtons socialMediaLinks={socialMediaLinks} />
                  )}
                  {phone && (
                    <a href={`tel:${phone}`} className={styles.phone}>
                      <PhoneImg className={styles.phoneImage} />
                      {formatPhoneNumber(phone)}
                    </a>
                  )}
                </div>
                {description && (
                  <p className={styles.description}>{description}</p>
                )}
                {onlineOrderingEnabled && <RestaurantHours />}
              </div>
            </Modal>
          )}
          {bannerUrl ? (
            <div
              className={styles.bannerImage}
              style={{ backgroundImage: `url(${bannerUrl})` }}
            />
          ) : (
            <div className={styles.noBannerImage} />
          )}
        </div>
      </div>
      <div className={styles.fulfillment}>
        <div className={styles.fulfillmentDisplay}>
          <FulfillmentSelector />
        </div>
      </div>
      {mealSelectorVariant && orderingAvailable && <MealSelectorPrompt />}
      <ReordersOrPopularItemsBanner hasReorders={hasReorders} />
      {promoBannerVariant && <PromoBanner />}
      <IntersectionObserver
        // rootMargin -60px is the height of fixed header plus 5px to trigger the onChange
        rootMargin='-65px 0px 0px 0px'
        onChange={showStickyShadowCallback}
      >
        <div className={styles.stickyShadowSentinel} />
      </IntersectionObserver>

      {shouldShowStickyNav() && (
        <MenuNavigationModule
          stickyRef={stickyRef}
          activeItem={activeItem}
          activeMenu={activeMenu}
          moreThanOneMenu={moreThanOneMenu}
          menus={menus}
          showStickyShadow={showStickyShadow}
        />
      )}
    </>
  )
}

/*
  Related posts that describe the issue and explain double displayName
  https://github.com/enzymejs/enzyme/pull/2137
  https://github.com/enzymejs/enzyme/issues/2196
*/

// to change displayName from RestaurantHeaderInner
RestaurantHeaderInner.displayName = 'RestaurantHeader'

export const RestaurantHeader = React.memo(RestaurantHeaderInner, isEqual)
// to change displayName from Memo(RestaurantHeader)
RestaurantHeader.displayName = 'RestaurantHeader'

const LocationShape = PropTypes.shape({
  address1: PropTypes.string,
  address2: PropTypes.string,
  city: PropTypes.string,
  state: PropTypes.string,
  phone: PropTypes.string
})

const ImageSetShape = PropTypes.shape({
  raw: PropTypes.string
})

const SocialMediaLinksShape = PropTypes.shape({
  twitterLink: PropTypes.string,
  facebookLink: PropTypes.string
})

const RestaurantShape = PropTypes.shape({
  location: LocationShape,
  guid: PropTypes.string,
  imageUrl: PropTypes.string,
  bannerUrls: ImageSetShape,
  minimumTakeoutTime: PropTypes.number,
  name: PropTypes.string,
  socialMediaLinks: SocialMediaLinksShape
})

const PublicApiShape = PropTypes.shape({
  calcHeaderOffset: PropTypes.func
})

RestaurantHeaderInner.propTypes = {
  restaurant: RestaurantShape,
  menus: PropTypes.array.isRequired,
  activeMenu: PropTypes.object.isRequired,
  menusLength: PropTypes.number,
  activeItem: PropTypes.string,
  publicApi: PropTypes.shape({ current: PublicApiShape })
}
